import { useState } from "react";
import { SeoHead } from "@/components/SeoHead";
import { PageTransition } from "@/components/layout/PageTransition";
import { Button } from "@/components/ui/button";
import { useSubmitContact, type ContactInput } from "@/hooks/use-contact";
import { useToast } from "@/hooks/use-toast";
import { MapPin, Phone, Mail, Globe, Send, ChevronDown, ChevronUp, Facebook, Twitter, Instagram, Linkedin, Youtube, AlertCircle } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { motion, AnimatePresence } from "framer-motion";
import { useCms } from "@/hooks/useCms";
import { useQuery } from "@tanstack/react-query";
import { FlagIcon } from "@/components/FlagIcon";

const DEFAULT_OFFICES = [
  { country: "Germany", address: "Unterseilberg 9, 94143, Grainet, Bayern" },
  { country: "USA", address: "20270, Plymouth Rd, Detroit, MI, 48228" },
  { country: "Canada", address: "1315, Finch Avenue, West, Toronto ON M3J 2G6" },
  { country: "UK", address: "1A Town Square, Erith, Kent, DA8 1RE" },
  { country: "Nigeria", address: "95, Ogudu Road, Ogudu, Lagos" },
];

const DEFAULT_SOCIAL = [
  { name: "Facebook", icon: "Facebook", url: "https://www.facebook.com/eliadasportsagency" },
  { name: "TikTok", icon: "TikTok", url: "https://www.tiktok.com/@eliadasportsagency" },
  { name: "Instagram", icon: "Instagram", url: "https://www.instagram.com/@eliadasportsagency" },
  { name: "YouTube", icon: "Youtube", url: "https://youtube.com/@eliadasportsagency" },
  { name: "X/Twitter", icon: "Twitter", url: "https://x.com/@eliadasports" },
];

const DEFAULT_FAQS = [
  { q: "How do I get scouted by Eliada Sports Agency?", a: "You can reach out through our contact form or attend one of our talent showcase events. Our scouting team evaluates players from local leagues, academies, and competitions across Africa and Europe." },
  { q: "What sports does Eliada represent?", a: "We primarily focus on football (soccer), but we also work with athletes across multiple sports disciplines including athletics and other team sports." },
  { q: "Is there a cost to join the agency?", a: "We offer various programs. Some development programs require investment; however, we also offer scholarship and sponsorship opportunities for exceptionally talented athletes." },
  { q: "How long does the transfer process take?", a: "International transfers typically take 4-12 weeks depending on the clubs involved, visa requirements, and negotiations. We guide athletes through every step." },
  { q: "Do you help with documentation and visas?", a: "Yes. We assist athletes in getting their documents legalized and work with government agencies to ensure smooth international travel and relocation." },
];

const SOCIAL_ICON_MAP: Record<string, React.ElementType | null> = {
  Facebook, Twitter, Instagram, Youtube, Linkedin, TikTok: null,
};

export default function Contact() {
  const { toast } = useToast();
  const contactMutation = useSubmitContact();
  const { getText, getMeta } = useCms();
  const { data: siteSettings } = useQuery<Record<string, string>>({
    queryKey: ["/api/settings/site"],
    queryFn: () => fetch("/api/settings/site").then(r => r.ok ? r.json() : {}),
  });
  const contactFormEnabled = siteSettings?.contact_form_enabled !== "false";

  const heroSubtitle = getText("contact_hero", "Reach out to our global offices for talent representation, partnerships, or general inquiries.");

  const contactInfoMeta = getMeta<{ phone?: string; phone2?: string; email?: string; website?: string }>("contact_info", {
    phone: "+49 15770 904476",
    phone2: "+234 70175 58595",
    email: "contact@eliadasportsagency.com",
    website: "www.eliadasportsagency.com",
  });

  const offices = getMeta<{ items: typeof DEFAULT_OFFICES }>("contact_offices", { items: DEFAULT_OFFICES }).items ?? DEFAULT_OFFICES;
  const socialLinks = getMeta<{ items: typeof DEFAULT_SOCIAL }>("contact_social", { items: DEFAULT_SOCIAL }).items ?? DEFAULT_SOCIAL;
  const faqs = getMeta<{ items: typeof DEFAULT_FAQS }>("contact_faqs", { items: DEFAULT_FAQS }).items ?? DEFAULT_FAQS;

  const [formData, setFormData] = useState<ContactInput>({ name: "", email: "", subject: "", message: "" });
  const [honeypot, setHoneypot] = useState("");
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (honeypot) return;
    contactMutation.mutate(formData, {
      onSuccess: () => {
        toast({ title: "Message Sent", description: "We've received your inquiry and will respond shortly." });
        setFormData({ name: "", email: "", subject: "", message: "" });
      },
      onError: (err: any) => {
        toast({ title: "Error", description: err.message || "Failed to send message. Please try again.", variant: "destructive" });
      }
    });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  return (
    <>
      <SeoHead
        title="Contact Us"
        description="Get in touch with Eliada Sports Agency. Reach out to our team for inquiries about athlete representation, partnerships, the football academy, or any other services."
        path="/contact"
      />
      <PageTransition className="pt-24 pb-24">
      {/* ── Hero Banner ── */}
      <div className="relative py-28 bg-secondary overflow-hidden border-b border-white/5">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1579952363873-27f3bade9f55?w=2000&auto=format&fit=crop" alt="" aria-hidden="true" className="w-full h-full object-cover object-center opacity-20" />
          <div className="absolute inset-0 bg-gradient-to-b from-background/70 via-background/40 to-background/90" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-primary/40 bg-primary/10 text-primary mb-6">
            <Mail size={13} />
            <span className="text-xs font-display tracking-widest uppercase font-semibold">Get In Touch</span>
          </motion.div>
          <h1 className="text-5xl md:text-7xl font-display font-bold text-white mb-5">CONTACT <span className="text-primary">US</span></h1>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto">{heroSubtitle}</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="h-16" />

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 mb-24">
          {contactFormEnabled ? (
          <div className="bg-secondary/50 rounded-2xl p-8 md:p-10 border border-white/5 shadow-2xl">
            <h3 className="text-2xl font-display font-bold text-white mb-8">Send a Message</h3>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Honeypot - hidden from real users */}
              <input type="text" name="website_url" value={honeypot} onChange={e => setHoneypot(e.target.value)} className="absolute -left-[9999px] w-0 h-0 overflow-hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-400">Your Name</label>
                  <input type="text" name="name" required value={formData.name} onChange={handleChange} className="w-full bg-background border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all" placeholder="John Doe" />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-400">Email Address</label>
                  <input type="email" name="email" required value={formData.email} onChange={handleChange} className="w-full bg-background border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all" placeholder="john@example.com" />
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-400">Subject</label>
                <input type="text" name="subject" required value={formData.subject} onChange={handleChange} className="w-full bg-background border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all" placeholder="How can we help?" />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-400">Message</label>
                <textarea name="message" required rows={5} value={formData.message} onChange={handleChange} className="w-full bg-background border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all resize-none" placeholder="Write your message here..." />
              </div>
              <Button type="submit" className="w-full gap-2" disabled={contactMutation.isPending}>
                <Send size={18} /> {contactMutation.isPending ? "Sending…" : "Send Message"}
              </Button>
            </form>
          </div>
          ) : (
          <div className="bg-secondary/50 rounded-2xl p-8 md:p-10 border border-white/5 shadow-2xl flex flex-col items-center justify-center text-center min-h-[400px]">
            <div className="w-16 h-16 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center mb-6">
              <AlertCircle className="w-8 h-8 text-primary/70" />
            </div>
            <h3 className="text-2xl font-display font-bold text-white mb-3">Contact Form Unavailable</h3>
            <p className="text-gray-400 mb-8 max-w-sm leading-relaxed">Our online contact form is temporarily unavailable. Please reach out to us directly using the contact details on the right.</p>
            <a
              href={`mailto:${contactInfoMeta.email ?? "contact@eliadasportsagency.com"}`}
              className="inline-flex items-center gap-2 bg-primary text-primary-foreground font-semibold px-6 py-3 rounded-xl hover:bg-primary/90 transition-colors"
            >
              <Mail size={16} /> Email Us Directly
            </a>
          </div>
          )}

          <div className="space-y-10">
            <div>
              <h3 className="text-2xl font-display font-bold text-white mb-6">Direct Contact</h3>
              <div className="space-y-6">
                <div className="flex items-center gap-4 bg-secondary p-5 rounded-xl border border-white/5 hover:border-primary/30 transition-colors">
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center text-primary shrink-0"><Phone size={20} /></div>
                  <div>
                    <p className="text-sm text-gray-400">Phone</p>
                    <p className="text-lg text-white font-medium">{contactInfoMeta.phone}{contactInfoMeta.phone2 ? ` | ${contactInfoMeta.phone2}` : ""}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 bg-secondary p-5 rounded-xl border border-white/5 hover:border-primary/30 transition-colors">
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center text-primary shrink-0"><Mail size={20} /></div>
                  <div>
                    <p className="text-sm text-gray-400">Email</p>
                    <p className="text-lg text-white font-medium">{contactInfoMeta.email}</p>
                  </div>
                </div>
                {contactInfoMeta.website && (
                  <a href={`https://${(contactInfoMeta.website ?? "").replace(/^https?:\/\//, "")}`} target="_blank" rel="noreferrer" className="flex items-center gap-4 bg-secondary p-5 rounded-xl border border-white/5 hover:border-primary/30 transition-colors group">
                    <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center text-primary shrink-0 group-hover:bg-primary/20 transition-colors"><Globe size={20} /></div>
                    <div>
                      <p className="text-sm text-gray-400">Website</p>
                      <p className="text-lg text-white font-medium group-hover:text-primary transition-colors">{contactInfoMeta.website}</p>
                    </div>
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="mb-24">
          <h2 className="text-3xl font-display font-bold text-center text-white mb-12">OUR GLOBAL <span className="text-primary">OFFICES</span></h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
            {offices.map((office, idx) => (
              <Card key={idx} className="bg-secondary/30 text-center hover:bg-secondary transition-colors border-white/5 flex flex-col items-center">
                <CardContent className="pt-6 flex flex-col items-center">
                  <div className="flex justify-center mb-3">
                    <FlagIcon country={office.country} size="lg" />
                  </div>
                  <h4 className="font-display font-bold text-lg text-white mb-2">{office.country}</h4>
                  <p className="text-sm text-gray-400 leading-relaxed">{office.address}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        <div className="mb-24 flex flex-col items-center">
          <h2 className="text-3xl font-display font-bold text-center text-white mb-12">FOLLOW <span className="text-primary">US</span></h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:flex lg:flex-wrap lg:justify-center gap-4 w-full max-w-6xl">
            {socialLinks.map((social, idx) => {
              const Icon = SOCIAL_ICON_MAP[social.icon];
              return (
                <a key={idx} href={social.url} target="_blank" rel="noreferrer" className="bg-secondary p-6 rounded-xl flex flex-col items-center justify-center text-center group hover:bg-primary/10 hover:border-primary/50 border border-white/5 transition-all duration-300 min-w-[160px]">
                  {Icon ? (
                    <Icon className="w-8 h-8 text-gray-400 group-hover:text-primary mb-4 transition-colors" />
                  ) : (
                    <span className="text-3xl mb-4 grayscale opacity-70 group-hover:grayscale-0 group-hover:opacity-100 transition-all">🎵</span>
                  )}
                  <h4 className="font-display font-semibold text-white mb-2">{social.name}</h4>
                  <span className="text-xs text-primary font-medium opacity-0 group-hover:opacity-100 transition-opacity translate-y-2 group-hover:translate-y-0 block">Follow Us</span>
                </a>
              );
            })}
          </div>
        </div>

        <div>
          <h2 className="text-3xl font-display font-bold text-center text-white mb-12">FREQUENTLY ASKED <span className="text-primary">QUESTIONS</span></h2>
          <div className="max-w-3xl mx-auto space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-secondary border border-white/5 rounded-xl overflow-hidden">
                <button onClick={() => setOpenFaq(openFaq === index ? null : index)} className="w-full flex items-center justify-between p-6 text-left focus:outline-none">
                  <span className="font-semibold text-white text-lg">{faq.q}</span>
                  {openFaq === index ? <ChevronUp className="text-primary shrink-0 ml-4" /> : <ChevronDown className="text-primary shrink-0 ml-4" />}
                </button>
                <AnimatePresence>
                  {openFaq === index && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="px-6 pb-6 text-gray-400 leading-relaxed overflow-hidden">
                      <div className="pt-2 border-t border-white/5 mt-2">{faq.a}</div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>
        </div>
      </div>
      </PageTransition>
    </>
  );
}
